import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "../../components/AdminLayout";
import { ip_back } from "../../config";
import { normalizeAppLang } from "../../utils/appLanguages";
import {
  BANDS,
  CB_STATUSES,
  SLOT_ROLES,
  classementLabel,
  clubOverrideRows,
} from "../../utils/levelBands";
import {
  canAccessLevelMatrix,
  canEditClubLevelMatrix,
  canEditDefaultLevelMatrix,
  clubIdOfUser,
} from "../../utils/levelBandsAccess";
import "./LevelBandMatrix.css";

const STARS = [0, 1, 2, 3, 4, 5];
const NIVEAUX = [1, 2, 3, 4, 5];

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  };
}

function blockName(block, lng) {
  return block?.[`cb_name_${lng}`] || block?.cb_name_en || block?.cb_name_fr || block?.cb_id || "";
}

function cellKey(band, cbId) {
  return `${band}|${cbId}`;
}

/**
 * Admin: which competency blocks each band works, reviews or keeps locked, plus
 * classement → band / drill niveau. Super Admin edits defaults; clubs may override.
 */
export default function LevelBandMatrix() {
  const { t, i18n } = useTranslation();
  const lng = normalizeAppLang(i18n.language);
  const info = JSON.parse(localStorage.getItem("user") || "{}");
  const allowed = canAccessLevelMatrix(info);
  const canDefaults = canEditDefaultLevelMatrix(info);
  const clubId = clubIdOfUser(info);
  const canClub = canEditClubLevelMatrix(info) && !!clubId;

  const [scope, setScope] = useState(canClub ? "club" : "default");
  const [defaults, setDefaults] = useState(null);
  const [rows, setRows] = useState([]);
  const [classements, setClassements] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [overrideOn, setOverrideOn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  const base = `${String(ip_back || "").replace(/\/?$/, "/")}api/level-bands`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const defRes = await axios.get(`${base}/matrix`, { headers: authHeaders() });
      const def = defRes.data?.data;
      setDefaults(def);
      setBlocks(def?.blocks || []);
      setClassements(def?.classements || []);
      if (scope === "club" && clubId) {
        const clubRes = await axios.get(`${base}/matrix?club_id=${encodeURIComponent(clubId)}`, {
          headers: authHeaders(),
        });
        const club = clubRes.data?.data;
        setRows(club?.band_cbs || []);
        setOverrideOn(!!club?.has_club_overrides);
      } else {
        setRows(def?.band_cbs || []);
        setOverrideOn(false);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("levelMatrixLoadError"));
    } finally {
      setLoading(false);
    }
  }, [base, scope, clubId, t]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const rowByCell = useMemo(() => {
    const map = new Map();
    for (const r of rows) map.set(cellKey(r.band_key, r.cb_id), r);
    return map;
  }, [rows]);

  const blockById = useMemo(() => new Map(blocks.map((b) => [String(b.cb_id), b])), [blocks]);

  const editable = scope === "default" ? canDefaults : canClub && overrideOn;

  const updateRow = (band, cbId, patch) => {
    setRows((prev) =>
      prev.map((r) =>
        r.band_key === band && String(r.cb_id) === String(cbId) ? { ...r, ...patch } : r
      )
    );
  };

  const toggleRole = (row, role) => {
    const roles = row.slot_roles || [];
    const next = roles.includes(role) ? roles.filter((x) => x !== role) : [...roles, role];
    updateRow(row.band_key, row.cb_id, { slot_roles: next });
  };

  const updateClassement = (classement, patch) => {
    setClassements((prev) => prev.map((c) => (c.classement === classement ? { ...c, ...patch } : c)));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (scope === "default") {
        await axios.put(
          `${base}/matrix`,
          { classements, band_cbs: rows },
          { headers: authHeaders() }
        );
      } else {
        const band_cbs = overrideOn ? clubOverrideRows(defaults?.band_cbs || [], rows) : [];
        await axios.put(
          `${base}/matrix/club/${encodeURIComponent(clubId)}`,
          { band_cbs },
          { headers: authHeaders() }
        );
      }
      setSnack({ severity: "success", message: t("levelMatrixSaved") });
      await load();
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e?.message || t("levelMatrixSaveError") });
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return <Navigate to="/Dashboard" replace />;
  }

  const bandRange = (band) => defaults?.bands?.find((b) => b.band_key === band)?.classement_range || "";

  const renderCell = (band, block) => {
    const row = rowByCell.get(cellKey(band, block.cb_id));
    if (!row) return <TableCell key={band} />;
    const prereq = row.prereq_cb_id ? blockById.get(String(row.prereq_cb_id)) : null;
    return (
      <TableCell key={band} className={`lbm-cell lbm-cell--${row.status}`}>
        <div className="lbm-cell__line">
          <Select
            size="small"
            value={row.status}
            disabled={!editable}
            onChange={(e) =>
              updateRow(band, block.cb_id, {
                status: e.target.value,
                ...(e.target.value === "intro" ? {} : { intro_from_classement: null }),
              })
            }
            aria-label={t("levelMatrixStatus")}
          >
            {CB_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`levelStatus_${s}`)}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={row.star_target}
            disabled={!editable}
            onChange={(e) => updateRow(band, block.cb_id, { star_target: Number(e.target.value) })}
            aria-label={t("levelMatrixStars")}
            renderValue={(v) => `★${v}`}
          >
            {STARS.map((s) => (
              <MenuItem key={s} value={s}>
                ★{s}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="lbm-cell__roles">
          {SLOT_ROLES.map((role) => {
            const on = (row.slot_roles || []).includes(role);
            return (
              <Chip
                key={role}
                size="small"
                label={t(`levelSlotRoleShort_${role}`)}
                title={t(`levelSlotRole_${role}`)}
                color={on ? "primary" : "default"}
                variant={on ? "filled" : "outlined"}
                onClick={editable ? () => toggleRole(row, role) : undefined}
                disabled={!editable && !on}
              />
            );
          })}
        </div>
        {row.status === "intro" ? (
          <div className="lbm-cell__prereq">
            {t("levelMatrixIntroFrom")}
            <Select
              size="small"
              value={row.intro_from_classement || ""}
              disabled={!editable}
              displayEmpty
              onChange={(e) => updateRow(band, block.cb_id, { intro_from_classement: e.target.value || null })}
              aria-label={t("levelMatrixIntroFrom")}
            >
              <MenuItem value="">{t("levelMatrixIntroWholeBand")}</MenuItem>
              {classements
                .filter((c) => c.band_key === band)
                .map((c) => (
                  <MenuItem key={c.classement} value={c.classement}>
                    {t("levelMatrixIntroFromValue", { classement: classementLabel(c.classement, t) })}
                  </MenuItem>
                ))}
            </Select>
          </div>
        ) : null}
        {prereq ? (
          <div className="lbm-cell__prereq">
            {t("levelMatrixPrereq", { name: blockName(prereq, lng) })}
            <Select
              size="small"
              value={row.prereq_stars ?? 0}
              disabled={!editable}
              onChange={(e) => updateRow(band, block.cb_id, { prereq_stars: Number(e.target.value) })}
              renderValue={(v) => `★${v}`}
              aria-label={t("levelMatrixPrereqStars")}
            >
              {STARS.map((s) => (
                <MenuItem key={s} value={s}>
                  ★{s}
                </MenuItem>
              ))}
            </Select>
          </div>
        ) : null}
      </TableCell>
    );
  };

  return (
    <AdminLayout>
      <Box className="lbm" sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          {t("levelMatrixTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("levelMatrixSubtitle")}
        </Typography>

        <div className="lbm-toolbar">
          {canDefaults && canClub ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scope}
              onChange={(_, v) => v && setScope(v)}
            >
              <ToggleButton value="club">{t("levelMatrixScopeClub")}</ToggleButton>
              <ToggleButton value="default">{t("levelMatrixScopeDefault")}</ToggleButton>
            </ToggleButtonGroup>
          ) : null}
          {scope === "club" ? (
            <FormControlLabel
              control={
                <Switch
                  checked={overrideOn}
                  onChange={(e) => {
                    setOverrideOn(e.target.checked);
                    if (!e.target.checked) setRows(defaults?.band_cbs || []);
                  }}
                />
              }
              label={t("levelMatrixClubOverride")}
            />
          ) : null}
          <Button variant="contained" onClick={save} disabled={saving || loading || (scope === "default" && !canDefaults)}>
            {saving ? t("saving") || "Saving…" : t("saveChanges")}
          </Button>
        </div>
        {scope === "club" && !overrideOn ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("levelMatrixClubOverrideOff")}
          </Alert>
        ) : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {loading ? (
          <Typography>{t("loading")}</Typography>
        ) : (
          <>
            <TableContainer component={Paper} className="lbm-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("levelMatrixBlock")}</TableCell>
                    {BANDS.map((band) => (
                      <TableCell key={band}>
                        <strong>{t(`levelBand_${band}`)}</strong>
                        <div className="lbm-range">{bandRange(band)}</div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blocks.map((block) => (
                    <TableRow key={block.cb_id}>
                      <TableCell className="lbm-block">
                        <span className="lbm-block__order">{block.learning_process_order || "–"}</span>
                        {blockName(block, lng)}
                      </TableCell>
                      {BANDS.map((band) => renderCell(band, block))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" sx={{ mt: 4, mb: 0.5 }}>
              {t("levelMatrixClassements")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("levelMatrixClassementsHint")}
            </Typography>
            <TableContainer component={Paper} className="lbm-table lbm-table--narrow">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("levelSelectClassement")}</TableCell>
                    <TableCell>{t("levelMatrixBand")}</TableCell>
                    <TableCell>{t("levelMatrixDrillNiveau")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classements.map((c) => (
                    <TableRow key={c.classement}>
                      <TableCell>{classementLabel(c.classement, t)}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={c.band_key}
                          disabled={scope !== "default" || !canDefaults}
                          onChange={(e) => updateClassement(c.classement, { band_key: e.target.value })}
                        >
                          {BANDS.map((b) => (
                            <MenuItem key={b} value={b}>
                              {t(`levelBand_${b}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={c.drill_niveau}
                          disabled={scope !== "default" || !canDefaults}
                          onChange={(e) => updateClassement(c.classement, { drill_niveau: Number(e.target.value) })}
                        >
                          {NIVEAUX.map((n) => (
                            <MenuItem key={n} value={n}>
                              {t("levelNiveau", { n })}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}>
          {snack ? (
            <Alert variant="filled" severity={snack.severity} onClose={() => setSnack(null)}>
              {snack.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Box>
    </AdminLayout>
  );
}
